/**
 * Настройки Saleus: окно (public/config.js) → сборка (VITE_*) → адрес по домену.
 *
 * В телефонной оболочке config.js вшит в сборку и не заменяется, поэтому
 * последний запасной вариант обязан быть боевым: без него приложение на
 * телефоне ходило бы «в никуда».
 */
export interface SaleusConfig {
  MAIN_DOMAIN: string;
  SUPABASE_URL: string;
  SUPABASE_ANON_KEY: string;
  CHATWOOT_URL: string;
  CRM_API_URL: string;
  TEAM_API_URL: string;
  TASK_API_URL: string;
  /** ГиперЧАТ: из него берём только распознавание речи («Текстом» у голосового). */
  CHAT_API_URL: string;
  APP_URL: string;
  /**
   * Push-уведомления (docs/PUSH.md). По умолчанию ВЫКЛЮЧЕНЫ: пока нет проекта
   * Firebase и аккаунта Apple, запрос разрешения и регистрация устройства
   * только напугали бы человека и ничего бы не дали. Включается
   * `PUSH_ENABLED: true` в public/config.js (или VITE_PUSH_ENABLED=true).
   */
  PUSH_ENABLED: boolean;
}

/** Публичный anon-ключ prod (тот же, что в Caddyfile у assistentus.unia.one). */
export const PROD_ANON_KEY =
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJyb2xlIjoiYW5vbiIsImlzcyI6InN1cGFiYXNlIiwiaWF0IjoxNzc0OTkzNTk1LCJleHAiOjE4MDY1Mjk1OTV9.XQPbDayV-MGYUen9vRUrDTH48kNWOq3_LEOfX9nMqrw';

type Источник = Partial<Record<string, string | boolean | undefined>>;

/** «true»/«1»/true → да; всё остальное (и пусто) → нет. */
export function parseFlag(v: string | boolean | undefined): boolean {
  if (typeof v === 'boolean') return v;
  return v === 'true' || v === '1';
}

/** Чистая сборка настроек — отдельно от окна, чтобы проверять в тестах. */
export function buildConfig(runtime: Источник = {}, env: Источник = {}): SaleusConfig {
  const mainDomain = String(runtime.MAIN_DOMAIN || env.VITE_MAIN_DOMAIN || 'unia.one');
  const pick = (key: string, envKey: string, fallback: string): string =>
    String(runtime[key] || env[envKey] || fallback);
  return {
    MAIN_DOMAIN: mainDomain,
    SUPABASE_URL: pick('SUPABASE_URL', 'VITE_SUPABASE_URL', `https://api.${mainDomain}`),
    SUPABASE_ANON_KEY: pick('SUPABASE_ANON_KEY', 'VITE_SUPABASE_ANON_KEY', PROD_ANON_KEY),
    CHATWOOT_URL: pick('CHATWOOT_URL', 'VITE_CHATWOOT_URL', `https://chatwoot.${mainDomain}`),
    CRM_API_URL: pick('CRM_API_URL', 'VITE_CRM_API_URL', `https://crm-api.${mainDomain}`),
    TEAM_API_URL: pick('TEAM_API_URL', 'VITE_TEAM_API_URL', `https://team.${mainDomain}`),
    TASK_API_URL: pick('TASK_API_URL', 'VITE_TASK_API_URL', `https://task.${mainDomain}`),
    CHAT_API_URL: pick('CHAT_API_URL', 'VITE_CHAT_API_URL', `https://chat.${mainDomain}/api/chat`),
    APP_URL: pick('APP_URL', 'VITE_APP_URL', `https://saleus.${mainDomain}`),
    PUSH_ENABLED: parseFlag(runtime.PUSH_ENABLED ?? env.VITE_PUSH_ENABLED),
  };
}

const окно = typeof window !== 'undefined' ? window.__UNIA_CONFIG__ ?? {} : {};
const config: SaleusConfig = buildConfig(
  окно,
  import.meta.env as unknown as Источник,
);

export default config;
